import moment from "moment";
import { ICuratorShowPromo } from "@src/types/show";
import { STORY_WEEKDAY_OPTIONS } from "./constants";
import { getDefaultStoryFields } from "./renderInstagramStory";
import type { StoryTemplateFields } from "./types";

/** Story slots run every 2 hours, so an airing is floored into its slot. */
const STORY_SLOT_HOURS = 2;

// STORY_WEEKDAY_OPTIONS starts on Monday; Date#getDay() starts on Sunday.
const toStoryDayLabel = (date: Date): string =>
  STORY_WEEKDAY_OPTIONS[(date.getDay() + 6) % 7];

// "00 AM" is midnight and "12 PM" is noon, matching STORY_TIME_OPTIONS. Hours
// are floored to the 2-hour slot they fall in so the value always exists in the
// Time dropdown — otherwise the select and the rendered story disagree.
const toStoryTimeParts = (
  date: Date
): { hour: string; period: "AM" | "PM" } => {
  const hour24 = date.getHours() - (date.getHours() % STORY_SLOT_HOURS);
  const period = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 === 0 || hour24 === 12 ? hour24 : hour24 % 12;

  return { hour: String(hour12).padStart(2, "0"), period };
};

/**
 * Prefill the Instagram Story template from a curator's show: its title, and
 * the weekday and hour of the airing it is promoted from. Anything the show
 * cannot answer keeps the placeholder default, and every field stays editable.
 */
export const buildStoryFieldsFromShow = (
  show: ICuratorShowPromo | undefined,
  curatorName: string
): StoryTemplateFields => {
  const fields = getDefaultStoryFields(curatorName);
  if (!show) return fields;

  // Kept in its natural casing — the canvas uppercases the title when it draws.
  const title = show.title?.trim();
  if (title) {
    fields.showTitle = title;
  }

  // Parsed as strict ISO-8601 (what Strapi returns) so anything else is simply
  // invalid rather than falling through to the browser's own Date parsing.
  const airing = show.airingStartTime
    ? moment(show.airingStartTime, moment.ISO_8601)
    : null;
  if (airing?.isValid()) {
    const date = airing.toDate();
    const { hour, period } = toStoryTimeParts(date);
    fields.dayLabel = toStoryDayLabel(date);
    fields.timeHour = hour;
    fields.timePeriod = period;
  }

  return fields;
};
